import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { FormNext } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import { StyledStepButton, StyledStepTooltip } from './StyledOnboardingComponents';

/**
 * Simple horizontal stepper used for the onboarding wizard.
 * Disabled states show a tooltip on hover to indicate how to enable the step.
 * Step config and disabled state is passed from the OnboardingWizard parent component.
 */
const OnboardingStepper = ({
  small,
  size,
  steps,
  activeKey,
  onStepClick,
  buttonJustify = null,
}) => {
  const { theme: { page, button } } = useAppTheme();
  const justify = size === 'medium' ? 'center' : 'between';

  return (
    <Box
      wrap
      direction="row"
      overflow="auto"
      justify={buttonJustify || justify}
      align="center"
      width="100%"
      style={{ gap: '0.375rem' }}
    >
      {steps.map((step, index) => {
        const isActive = step.key === activeKey;
        const isDisabled = step.disabled;
        const labelColor = isActive ? page.altHighlight : page.primaryText;
        const bgColor = isActive ? button.selectBg : page.altBg;
        const stepNumberColor = isActive ? 'white' : page.primaryText;

        const tooltipId = `step-${step.key}-tooltip`;
        const tooltipMessage = step.tooltip || (isDisabled ? 'Enable this in the Services step' : '');

        return (
          <React.Fragment key={step.key}>
            <Box
              direction={small ? 'column' : 'row'}
              align="center"
              width={small ? undefined : 'auto'}
              style={small ? { flex: '1 1 calc(50% - 0.375rem)', minWidth: 0 } : undefined}
            >
              <StyledStepButton
                level="plain"
                onClick={() => {
                  if (!isDisabled) onStepClick(step.key);
                }}
                disabled={isDisabled}
                isSmall={small}
                hoverBg={isDisabled ? 'transparent' : button.selectBg}
                labelColor={labelColor}
                hoverTextColor={page.altHighlight}
                bgColor={bgColor}
                stepNumberColor={stepNumberColor}
                isActive={isActive}
                data-tip={isDisabled ? tooltipMessage : ''}
                data-for={isDisabled ? tooltipId : ''}
                label={(
                  <Box direction="row" gap="0.5rem" align="center">
                    <Box
                      className="step-number"
                      width="1.1rem"
                      height="1.1rem"
                      round="full"
                      align="center"
                      justify="center"
                      background={isActive ? button.primaryBg : page.altBg}
                    >
                      <Text size="0.8rem" weight={600}>
                        {index + 1}
                      </Text>
                    </Box>
                    <Text className="step-label" size="0.8rem" weight={600}>
                      {step.label}
                    </Text>
                  </Box>
                )}
              />
              {isDisabled && (
                <StyledStepTooltip
                  id={tooltipId}
                  place="bottom"
                  effect="solid"
                  backgroundColor={page.cardBg}
                  arrowColor={page.cardBg}
                  textColor={page.primaryText}
                  delayShow={300}
                />
              )}
            </Box>
            {!small && (index < steps.length - 1) && (
              <FormNext size="1rem" color={isActive ? page.altHighlight : page.primaryText} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

OnboardingStepper.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    tooltip: PropTypes.string,
  })).isRequired,
  activeKey: PropTypes.string.isRequired,
  onStepClick: PropTypes.func.isRequired,
  small: PropTypes.bool.isRequired,
  size: PropTypes.string.isRequired,
  buttonJustify: PropTypes.string,
};

export default OnboardingStepper;

